"use client";

import GridCellProtocol from "@/components/Grid/Cell/Protocol";
import type { SupDataGridColumn, SupSingleVaultV3 } from "@/types";
import React from "react";

import GridCellAuthenticated from "@/components/Grid/Cell/Authenticated";
import GridCellVault from "@/components/Grid/Cell/Vault";
import GridCellCropped from "@/components/Grid/Cell/Cropped";
import GridCellRewardApy from "@/components/Grid/Cell/RewardApy";
import GridCellAccountPositionRewards from "@/components/Grid/Cell/Account/PositionRewards";
import GridHeaderCellApy from "@/components/Grid/HeaderCell/Apy";
import GridCellAvailability, { AVAILABILITY_DICT } from "@/components/Grid/Cell/Availability";
import GridCellYieldType from "@/components/Grid/Cell/YieldType";
import GridCellDate from "@/components/Grid/Cell/Date";
import UiLabelSharpe from "@/components/Ui/Label/Sharpe";
import UiLabelTvl from "@/components/Ui/Label/Tvl";
import GridCellToggleFavorite from "@/components/Grid/Cell/ToggleFavorite";
import DepositButton from "@/components/Adapter/DepositButton";

import { type Column, SupDataGrid, SupTracker } from "@repo/core-ui";

import { getSortingDate, getVaultSharpe, getVaultTvl, getNetVaultApy, getProtocolName } from "@/utils";
import { useFilterGridColumns } from "@/store";
import useRewardSettings from "@/hooks/data/useRewardSettings";

interface VaultTableProps {
  vaults: SupSingleVaultV3[];
  hideColumns?: string[];
  isFiltered?: boolean;
  isLoading?: boolean;
  controlledColumns?: SupDataGridColumn[];
}

export default function VaultTable({ vaults, controlledColumns, hideColumns = [], isFiltered = false, isLoading = false }: VaultTableProps) {
  const rewardSettings = useRewardSettings();
  const columns: Column<SupSingleVaultV3>[] = [
    {
      width: 20,
      field: "favorite",
      resizable: false,
      sortable: false,
      headerName: "",
      align: "center",
      renderCell: row => <GridCellToggleFavorite singleVault={row} />,
    },
    {
      field: "friendly_name",
      headerName: "Vault",
      headerCellClassName: "pl-4",
      width: 330,
      renderCell: row => <GridCellVault singleVault={row} />,
    },
    {
      field: "protocol",
      headerName: "Protocol",
      resizable: false,
      width: 160,
      valueGetter: row => getProtocolName(row),
      renderCell: row => <GridCellProtocol vault={row} />,
    },
    {
      headerName: "ID",
      field: "id",
      draggable: false,
      resizable: false,
      renderCell: row => <GridCellCropped value={row.id} front={3} back={3} />,
    },
    {
      field: "tvl",
      headerName: "TVL",
      align: "right",
      width: 96,
      type: "number",
      valueGetter: row => getVaultTvl(row),
      renderCell: row => <UiLabelTvl vault={row} />,
    },
    {
      field: "rewards",
      headerName: "Rewards", //      minWidth: 100,
      align: "left",
      sortable: false,
      width: 192,
      valueGetter: row => row.rewards?.reduce((acc, reward) => acc + reward.address, ""),
      renderCell: row => <GridCellAccountPositionRewards singlePosition={{ vault: row, chain_id: row.chain.id }} />,
    },
    {
      field: "yield_type",
      headerName: "Yield Type",
      width: 160,
      renderCell: row => <GridCellYieldType vault={row} />,
    },
    {
      field: "is_authenticated",
      headerName: "Vault Status",
      type: "string",
      valueGetter: row => row.is_authenticated || row.protocol?.is_authenticated,
      renderCell: row => <GridCellAuthenticated vault={row} />,
    },
    {
      field: "is_authenticated_protocol",
      headerName: "Protocol Status",
      type: "string",
      valueGetter: row => row.protocol?.is_authenticated ?? false,
      renderCell: row => (
        <SupTracker variant={row.protocol?.is_authenticated ? "success" : "primary"}>{row.protocol?.is_authenticated ? "Authenticated" : "Unauthenticated"}</SupTracker>
      ),
    },
    {
      field: "apy_day",
      headerName: "24H APY",
      align: "right",
      type: "number",
      width: 132,
      resizable: false,
      valueGetter: row => getNetVaultApy(row, rewardSettings, "day"),
      renderCell: row => <GridCellRewardApy vault={row} period="day" />,
    },
    {
      field: "apy_week",
      headerName: "7D APY",
      align: "right",
      type: "number",
      width: 132,
      resizable: false,
      valueGetter: row => getNetVaultApy(row, rewardSettings, "week"),
      renderCell: row => <GridCellRewardApy vault={row} period="week" />,
    },
    {
      field: "apy",
      headerName: <GridHeaderCellApy />,
      align: "right",
      type: "number",
      width: 132,
      resizable: false,
      valueGetter: row => getNetVaultApy(row, rewardSettings),
      renderCell: row => <GridCellRewardApy vault={row} />,
    },
    {
      field: "sharpe",
      headerName: "Sharpe",
      type: "number",
      align: "right",
      width: 132,
      valueGetter: row => getVaultSharpe(row),
      renderCell: row => <UiLabelSharpe vault={row} />,
    },
    {
      field: "availability",
      headerName: "Availability",
      type: "number",
      width: 140,
      align: "right",
      valueGetter: row => AVAILABILITY_DICT[row.availability],
      renderCell: row => <GridCellAvailability vault={row} />,
    },
    {
      field: "created_at",
      headerName: "List date",
      width: 140,
      align: "right",
      type: "number",
      valueGetter: row => getSortingDate(row.created_at),
      renderCell: row => <GridCellDate object={row} />,
    },
    {
      field: "actions",
      headerName: "",
      resizable: false,
      draggable: false,
      sortable: false,
      align: "right",
      minWidth: 180,
      renderCell: row => <DepositButton singleVault={row} />,
    },
  ];

  const { excludeColumns, defaultSorting, setSorting } = useFilterGridColumns("Vaults", "tvl", "friendly_name", "DESC", "ASC", hideColumns, controlledColumns);

  return (
    <div className="sup-grid-container-page w-full">
      <SupDataGrid
        className="w-full min-w-full"
        enableVirtualization
        rowId={row => row.id}
        rowHeight={88}
        data={vaults}
        excludeColumns={excludeColumns}
        columns={columns}
        defaultSorting={defaultSorting}
        onSortChange={sorting => setSorting(sorting[0])}
        filtered={isFiltered}
        loading={isLoading}
      />
    </div>
  );
}
